import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowUpRight, Building2, Heart, Factory, ShoppingBag, Landmark,
  Cpu, Zap, Truck, Sparkles, Share2, Check, AlertTriangle, Wrench, TrendingUp,
  Calendar
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import api, { resolveMedia } from '../lib/api';

const industryIcon = {
  'Financial Services': Building2,
  'Healthcare': Heart,
  'Manufacturing': Factory,
  'Retail & E-commerce': ShoppingBag,
  'Government & Public Sector': Landmark,
  'Technology & SaaS': Cpu,
  'Energy & Utilities': Zap,
  'Logistics': Truck
};

const Skeleton = () => (
  <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-36 pb-20 animate-pulse">
    <div className="h-4 w-32 bg-white/5 rounded mb-6"></div>
    <div className="h-12 bg-white/5 rounded mb-4 w-3/4"></div>
    <div className="h-6 bg-white/5 rounded w-1/2"></div>
    <div className="mt-10 aspect-[16/8] bg-white/5 rounded-3xl"></div>
  </div>
);

const Paragraph = ({ text }) => (
  <div className="space-y-4 text-slate-300 leading-relaxed">
    {(text || '').split(/\n{2,}/).filter(Boolean).map((p, i) => (
      <p key={i}>{p}</p>
    ))}
  </div>
);

const CaseStudyDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [study, setStudy] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    api.get(`/case-studies/${encodeURIComponent(slug)}`).then((r) => {
      if (!alive) return;
      setStudy(r.data);
      // load related from the same industry
      api.get(`/case-studies?industry=${encodeURIComponent(r.data.industry)}`).then((rr) => {
        if (!alive) return;
        setRelated((rr.data || []).filter((c) => c.id !== r.data.id).slice(0, 3));
      }).catch(() => {});
    }).catch(() => alive && setNotFound(true)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: study?.title, url }); return; } catch { /* fallthrough */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied', description: 'Share it wherever you like.' });
    } catch {
      toast({ title: 'Could not copy link' });
    }
  };

  if (loading) return (<main className="bg-[#05080f] min-h-screen"><Navbar /><Skeleton /><Footer /></main>);

  if (notFound || !study) {
    return (
      <main className="bg-[#05080f] min-h-screen">
        <Navbar />
        <section className="pt-40 pb-24">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <div className="text-xs tracking-[0.3em] text-amber-300/80 mb-3">CASE STUDY</div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold text-white mb-4">We couldn&apos;t find that case study.</h1>
            <p className="text-slate-400 mb-8">The link might be old, or the study is in draft. Browse the ones we&apos;ve published.</p>
            <Button onClick={() => navigate('/#case-studies')} className="btn-gradient rounded-full h-11 px-6 border-0" data-testid="cs-back-home">
              Browse case studies <ArrowUpRight size={16} className="ml-1" />
            </Button>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  const Icon = industryIcon[study.industry] || Sparkles;
  const created = study.created_at ? new Date(study.created_at) : null;

  return (
    <main className="bg-[#05080f] min-h-screen" data-testid="case-study-detail">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-36 pb-16 md:pt-44 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.18),transparent_60%)]"></div>
        <div className="perspective-grid"></div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <Link to="/#case-studies" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-300 text-sm mb-6" data-testid="cs-detail-back">
            <ArrowLeft size={14} /> All case studies
          </Link>

          <div className="flex items-center gap-2 flex-wrap mb-5">
            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-200 border border-amber-500/25">
              <Icon size={12} /> {study.industry}
            </span>
            {study.featured && (
              <span className="text-[11px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-100 border border-amber-500/40">
                Featured
              </span>
            )}
            {study.client && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-slate-300 border border-white/10">
                Client: {study.client}
              </span>
            )}
          </div>

          <h1 className="font-display text-4xl md:text-6xl font-semibold text-white leading-[1.05] tracking-tight" data-testid="cs-detail-title">
            {study.title}
          </h1>

          {study.outcome && (
            <p className="mt-5 text-slate-300 text-lg md:text-xl leading-relaxed max-w-3xl">
              {study.outcome}
            </p>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3 text-slate-500 text-xs">
            {created && (
              <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> Published {created.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
            )}
            <button onClick={share} className="inline-flex items-center gap-1.5 text-amber-300 hover:text-amber-200" data-testid="cs-detail-share">
              <Share2 size={12} /> Share this case study
            </button>
          </div>

          {study.thumbnail && (
            <div className="mt-10 aspect-[16/8] rounded-3xl overflow-hidden border border-white/10 relative">
              <img
                src={resolveMedia(study.thumbnail)}
                alt={study.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>
      </section>

      {/* METRICS BAND */}
      {Array.isArray(study.metrics) && study.metrics.length > 0 && (
        <section className="relative py-8">
          <div className="max-w-4xl mx-auto px-6 lg:px-10">
            <div className="card-dark rounded-3xl p-6 md:p-8 grid gap-6 grid-cols-1 md:grid-cols-3">
              {study.metrics.slice(0, 3).map((m) => (
                <div key={m} className="text-center md:text-left">
                  <TrendingUp className="text-amber-400 mb-2 mx-auto md:mx-0" size={20} />
                  <div className="font-display text-2xl md:text-3xl font-semibold text-white leading-tight">{m}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BODY */}
      <section className="relative py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 space-y-14">
          {study.challenge && (
            <div>
              <div className="section-label mb-6">The challenge</div>
              <div className="flex items-start gap-4">
                <div className="hidden md:flex h-11 w-11 shrink-0 rounded-2xl bg-amber-500/10 border border-amber-500/25 items-center justify-center">
                  <AlertTriangle className="text-amber-400" size={20} strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight mb-4">
                    What was <span className="gradient-text">breaking</span>.
                  </h2>
                  <Paragraph text={study.challenge} />
                </div>
              </div>
            </div>
          )}

          {study.solution && (
            <div>
              <div className="section-label mb-6">The solution</div>
              <div className="flex items-start gap-4">
                <div className="hidden md:flex h-11 w-11 shrink-0 rounded-2xl bg-amber-500/10 border border-amber-500/25 items-center justify-center">
                  <Wrench className="text-amber-400" size={20} strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight mb-4">
                    What we <span className="gradient-text">built</span>.
                  </h2>
                  <Paragraph text={study.solution} />
                </div>
              </div>
            </div>
          )}

          {study.outcome && (
            <div>
              <div className="section-label mb-6">The outcome</div>
              <div className="flex items-start gap-4">
                <div className="hidden md:flex h-11 w-11 shrink-0 rounded-2xl bg-amber-500/10 border border-amber-500/25 items-center justify-center">
                  <TrendingUp className="text-amber-400" size={20} strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight mb-4">
                    What <span className="gradient-text">changed</span>.
                  </h2>
                  <Paragraph text={study.outcome} />

                  {Array.isArray(study.metrics) && study.metrics.length > 0 && (
                    <ul className="mt-6 space-y-2">
                      {study.metrics.map((m) => (
                        <li key={m} className="flex gap-2 text-slate-200 text-sm">
                          <Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> {m}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {Array.isArray(study.tags) && study.tags.length > 0 && (
            <div className="pt-6 border-t border-white/5">
              <div className="text-[11px] tracking-widest text-slate-500 mb-3">TAGS</div>
              <div className="flex flex-wrap gap-2">
                {study.tags.map((t) => (
                  <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-slate-300 border border-white/10">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="card-dark rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.18),transparent_60%)]"></div>
            <div className="relative">
              <Sparkles className="text-amber-300 mx-auto mb-3" size={26} />
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mb-4">
                Want an outcome like this <span className="gradient-text">for your team?</span>
              </h2>
              <p className="text-slate-300 max-w-xl mx-auto mb-8 leading-relaxed">
                Book a 30-minute walkthrough. We&apos;ll map your environment and show you exactly what we&apos;d change.
              </p>
              <Link to="/#contact">
                <Button className="btn-gradient rounded-full h-12 px-8 border-0 text-base" data-testid="cs-detail-cta">
                  Talk to our team
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="relative py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="section-label mb-6">More from {study.industry}</div>
            <h2 className="font-display text-2xl md:text-4xl font-semibold text-white tracking-tight leading-tight mb-10">
              Related <span className="gradient-text">case studies.</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-5">
              {related.map((c) => {
                const RIcon = industryIcon[c.industry] || Sparkles;
                return (
                  <Link
                    key={c.id}
                    to={`/case-studies/${c.slug || c.id}`}
                    className="card-dark rounded-3xl overflow-hidden hover:border-amber-500/30 transition-colors group flex flex-col"
                    data-testid={`related-case-${c.id}`}
                  >
                    <div className="relative aspect-[16/9] bg-gradient-to-br from-amber-500/10 to-transparent border-b border-white/5 overflow-hidden">
                      {c.thumbnail ? (
                        <img src={resolveMedia(c.thumbnail)} alt={c.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <RIcon className="text-amber-300/40" size={44} strokeWidth={1.3} />
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="text-white font-display text-lg font-semibold group-hover:text-amber-200 transition-colors">{c.title}</h3>
                      {c.outcome && <p className="text-slate-400 text-sm mt-2 line-clamp-2">{c.outcome}</p>}
                      <div className="mt-auto pt-4 text-amber-300 text-sm inline-flex items-center gap-1.5">
                        Read case study <ArrowUpRight size={14} />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
};

export default CaseStudyDetailPage;
